import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Pencil, Trash2, Github, ArrowLeft, ExternalLink, Lock, Globe } from 'lucide-react';
import { Project as ProjectType, Skill } from '../types';
import { localized, mediaUrl, hasRichContent, pitchOf, titleOf } from '../utils';
import { BACKEND_URL } from '../config';
import SkillBadge from '../components/SkillBadge';
import MediaCarousel from '../components/MediaCarousel';
import PageMeta from '../components/PageMeta';
import { ProjectSkeleton } from '../components/Skeletons';
import { AppleIcon, GooglePlayIcon } from '../components/ProjectLinks';

interface ProjectPageProps {
  authenticated: boolean;
}

const linkBtn = 'h-11 px-5 inline-flex items-center gap-2 rounded-full text-sm font-semibold transition-colors';
const sectionTitle = 'font-display font-extrabold tracking-[-0.03em] text-[28px] text-ink mb-5';

export default function ProjectPage({ authenticated }: ProjectPageProps) {
  const { strid } = useParams<{ strid: string }>();
  const [project, setProject] = useState<ProjectType | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Opened in a new tab: no history to go back to, so fall back to the projects list.
  const back = () => location.key === 'default' ? navigate('/projects') : navigate(-1);

  useEffect(() => {
    if (!strid) return;
    const token = localStorage.getItem('token');
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${BACKEND_URL}/api/projects/${strid}`, { headers })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setProject)
      .catch(() => { toast.error(String(t('error_loading_project'))); navigate(-1); });
  }, [strid, t, navigate]);

  if (!project) return <ProjectSkeleton />;

  return <ProjectView project={project} authenticated={authenticated} onBack={back} />;
}

/** Project page body, also used by the form preview overlay. */
export function ProjectView({ project, authenticated, onBack }: { project: ProjectType; authenticated: boolean; onBack: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const handleDelete = () => {
    if (!window.confirm(t('confirm_delete_project') as string)) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${BACKEND_URL}/api/projects/${project.strid}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
      .then(() => { window.location.href = '/projects'; })
      .catch(console.error);
  };

  const base = `${BACKEND_URL}/projects/${project.strid}`;
  const logo = mediaUrl(project.logo, base);
  const banner = mediaUrl(project.banner, base);
  const roles = (project.roles || []).map(r => localized(r.name, lang)).filter(Boolean);
  const byCategory = (cat: string) => (project.skills || [])
    .filter(s => s.category?.fr === cat)
    .sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang));
  const stackGroups: [string, Skill[]][] = [
    [t('skills_languages'), byCategory('Langage')],
    [t('skills_technologies'), byCategory('Technologie')],
  ];
  const otherSkills = [...byCategory('Compétence'), ...byCategory('Soft skill')];
  const endLabel = project.ongoing ? t('on_going') : project.end_date;
  // [label, line 1, line 2, wraps]: only the roles list wraps, the other columns stay as narrow as their content
  type Meta = [string, string, string, boolean];
  const meta = ([
    [t('type'), localized(project.type, lang), '', false],
    project.not_started
      ? [t('dates'), t('not_started'), '', false]
      : [t('dates'), project.start_date ?? '', endLabel ? `→ ${endLabel}` : '', false],
    [t('roles'), roles.join(', '), '', true],
    [t('team'), project.team ? String(project.team) : '', '', false],
    [t('version'), project.version ?? '', project.last_updated ? `${t('updated_on')} ${project.last_updated}` : '', false],
  ] as Meta[]).filter(([, v1]) => v1);
  const sortedLinks = (links: { title: Record<string, string>; url: string }[] = []) =>
    [...links].sort((a, b) => (localized(a.title, lang) || a.url).localeCompare(localized(b.title, lang) || b.url, lang));

  return (
    <div className="pt-16 pb-20">
      <PageMeta title={titleOf(project, lang)} description={pitchOf(localized(project.description, lang))} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 flex items-center justify-between">
        <button onClick={onBack} className="h-11 px-3 -ml-3 inline-flex items-center gap-1.5 rounded-full text-sm text-zinc-400 hover:text-ink transition-colors">
          <ArrowLeft size={16} /> {t('back')}
        </button>
        {authenticated && (
          <div className="flex gap-1">
            <Link to={`/projects/edit/${project.strid}`} className="w-11 h-11 rounded-full flex items-center justify-center text-zinc-400 hover:text-ink hover:bg-surface" aria-label={t('edit_project') as string}><Pencil size={16} /></Link>
            <button onClick={handleDelete} className="w-11 h-11 rounded-full flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-surface" aria-label="Delete"><Trash2 size={16} /></button>
          </div>
        )}
      </div>

      <header className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 flex flex-wrap items-center gap-8 lg:gap-10">
        {logo && <img src={logo} alt="" className="w-40 h-40 sm:w-56 sm:h-56 flex-none rounded-[32px] object-cover" />}
        <div className="flex-1 basis-[320px] min-w-0">
          {!project.is_public && (
            <p className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-surface text-zinc-400"><Lock size={12} /> {t('private_project')}</p>
          )}
          <h1 className="font-display font-extrabold tracking-[-0.04em] text-5xl sm:text-7xl leading-[0.95] break-words">{titleOf(project, lang)}</h1>
          {(project.subtitle?.[lang]?.trim() || hasRichContent(project.description, lang)) && (
            <p className="mt-5 text-lg sm:text-xl leading-relaxed text-muted max-w-2xl">{project.subtitle?.[lang]?.trim() || pitchOf(localized(project.description, lang), 200)}</p>
          )}
          <div className="mt-7 flex flex-wrap gap-2.5">
            {project.app_store_url && <a href={project.app_store_url} target="_blank" rel="noopener noreferrer" className={`${linkBtn} bg-ink text-base hover:bg-primary hover:text-white`}><AppleIcon size={15} /> App Store</a>}
            {project.play_store_url && <a href={project.play_store_url} target="_blank" rel="noopener noreferrer" className={`${linkBtn} bg-ink text-base hover:bg-primary hover:text-white`}><GooglePlayIcon size={14} /> Google Play</a>}
            {project.website_url && <a href={project.website_url} target="_blank" rel="noopener noreferrer" className={`${linkBtn} bg-ink text-base hover:bg-primary hover:text-white`}><Globe size={15} /> {t('website')}</a>}
            {project.github_url && <a href={project.github_url} target="_blank" rel="noopener noreferrer" className={`${linkBtn} border-[1.5px] border-ink text-ink hover:bg-surface`}><Github size={15} /> GitHub</a>}
            {sortedLinks(project.external_links).map((link, idx) => (
              <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className={`${linkBtn} border border-zinc-700 text-zinc-200 hover:text-ink`}>
                <ExternalLink size={14} /> {localized(link.title, lang) || link.url}
              </a>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="overflow-x-auto border-y border-line">
          <table className="w-full text-left">
            <thead>
              <tr>{meta.map(([k]) => <th key={k} className="pt-5 pb-1.5 pr-8 last:pr-0 text-xs font-normal uppercase tracking-[0.08em] text-zinc-500 whitespace-nowrap">{k}</th>)}</tr>
            </thead>
            <tbody>
              <tr className="align-top">
                {meta.map(([k, v1, v2, wraps]) => (
                  <td key={k} className={`pb-5 pr-8 last:pr-0 ${wraps ? 'min-w-[200px] w-full' : 'whitespace-nowrap'}`}>
                    <p className="text-[15px] font-medium text-ink">{v1}</p>
                    {v2 && <p className="mt-0.5 text-[13px] text-muted">{v2}</p>}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        {banner && <img src={banner} alt="" className="mt-10 w-full aspect-[21/9] object-cover rounded-3xl bg-surface" />}
      </div>

      {hasRichContent(project.description, lang) && (
        <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-14 prose-content text-[17px] leading-[1.75]" dangerouslySetInnerHTML={{ __html: localized(project.description, lang) }} />
      )}

      {(project.images.length > 0 || project.videos.length > 0) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-14">
          <MediaCarousel images={project.images} videos={project.videos} strid={project.strid} />
        </section>
      )}

      {(stackGroups.some(([, list]) => list.length) || otherSkills.length > 0) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
          <h2 className={sectionTitle}>Stack</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {stackGroups.filter(([, list]) => list.length).map(([label, list]) => (
              <div key={label}>
                <p className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-2.5">{label}</p>
                <div className="flex flex-wrap gap-2">{list.map(s => <SkillBadge key={s.id} skill={s} />)}</div>
              </div>
            ))}
          </div>
          {otherSkills.length > 0 && (
            <details className="mt-6 group">
              <summary className="cursor-pointer min-h-[44px] flex items-center text-sm font-semibold text-primary-light">
                {t('other_skills', { count: otherSkills.length })}
              </summary>
              <div className="flex flex-wrap gap-2 pt-2">{otherSkills.map(s => <SkillBadge key={s.id} skill={s} />)}</div>
            </details>
          )}
        </section>
      )}

      {authenticated && (project.private_links || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12"><div className="rounded-2xl bg-surface p-5">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.08em] text-zinc-500 mb-3"><Lock size={12} /> {t('private_links')}</p>
          <div className="flex flex-wrap gap-2">
            {sortedLinks(project.private_links).map((link, idx) => (
              <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className={`${linkBtn} border border-zinc-700 text-zinc-200 hover:text-ink`}>
                <ExternalLink size={14} /> {localized(link.title, lang) || link.url}
              </a>
            ))}
          </div>
        </div></section>
      )}
    </div>
  );
}
